import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { uniqBy } from 'lodash';

import {
  useBookTripStore,
  useItineraryStore,
  usePaxStore,
  useShareWidgetStore,
  useStatsigEventsStore,
  useTripStore,
  useGlobalCurrencyStore,
  useBrowserStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import { assertValue } from '@libs/utils';
import type { AppButtonState } from '@libs/ui';
import { getMarketOrigin } from '@libs/search-service-smart-search';

import { TripSummaryPriceType } from '../../models';
import type {
  TripSummaryPrice,
  TripSummarySegment,
  TripSummarySegmentPrice,
} from '../../models';
import { useTripOverviewModalsStore } from '../tripOverviewModalsStore';
import { useUpsellStore } from '../upsell/upsellStore';

import {
  getBookingButtonState,
  getTravelDateRange,
  getTripGrossPrice,
  getTripSummaryPrice,
  getTripSummaryPriceSegments,
  getTripTotalPrice,
  isEveryIngredientAvailableFn,
} from './tripSummaryStoreLogic';

export const useTripSummaryStore = defineStore('TripSummaryStore', () => {
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();

  const itineraryStore = useItineraryStore();
  const shareWidgetStore = useShareWidgetStore();
  const bookTripStore = useBookTripStore();
  const statsigEventsStore = useStatsigEventsStore();
  const preferredTimeStore = usePreferredTimeStore();
  const tripOverviewModalsStore = useTripOverviewModalsStore();
  const paxStore = usePaxStore();
  const upsellStore = useUpsellStore();
  const globalCurrencyStore = useGlobalCurrencyStore();
  const browserStore = useBrowserStore();
  // State
  const isTripSummaryVisible = ref<boolean>(true);

  // Getters
  const tripPriceSegments = computed<TripSummarySegment[]>(() =>
    getTripSummaryPriceSegments({
      chronologicalItinerary: tripStore.chronologicalItinerary,
      numberOfTravelers: paxStore.numberOfTravelers,
      addons: upsellStore.userSelectedFerryTicketCategory,
      moneyFormatterFn: globalCurrencyStore.formatMoney,
      numberOfDestinations: recipeStore.stayOnlyRecipe.length,
      paxText: paxStore.getPaxesButtonDetailedText,
      isMobile: !browserStore.isLargerThan1199px,
    })
  );

  const travelDateRange = computed<string>(() =>
    getTravelDateRange(tripStore.chronologicalItinerary)
  );

  const tripSummaryPrice = computed<TripSummaryPrice | undefined>(() =>
    getTripSummaryPrice({
      totalPrice: tripStore.selectedItineraryWithStaticInfo?.totalPrice,
      totalPriceWithAlternative:
        tripStore.selectedItineraryWithStaticInfo?.totalPriceWithAlternative,
      grossPrice: tripStore.selectedItineraryWithStaticInfo?.grossPrice,
      grossPriceWithAlternative:
        tripStore.selectedItineraryWithStaticInfo?.grossPriceWithAlternative,
    })
  );

  const tripTotalPrice = computed<number>(() =>
    getTripTotalPrice(tripSummaryPrice.value)
  );

  const tripGrossPrice = computed<number>(() =>
    getTripGrossPrice(tripSummaryPrice.value)
  );

  const tripSummaryCurrency = computed<string>(
    () => tripSummaryPrice.value?.totalPrice?.currency ?? ''
  );

  const tripTotalAddonsPrice = computed<number>(() => {
    const prices: TripSummarySegmentPrice[] = tripPriceSegments.value.flatMap(
      (segment: TripSummarySegment) => segment.price
    );

    const addons = prices.filter(
      (segmentPrice: TripSummarySegmentPrice) => segmentPrice.type === 'addon'
    );

    const total = addons.reduce(
      (acc: number, segmentPrice: TripSummarySegmentPrice) =>
        acc + segmentPrice.amount,
      0
    );
    return total;
  });

  const isEveryIngredientAvailable = computed<boolean>(
    () =>
      isEveryIngredientAvailableFn(itineraryStore.selectedItinerary) &&
      tripTotalPrice.value > 0
  );

  const tripSummaryLoader = computed<number>(
    () =>
      uniqBy(recipeStore.recipe, (ingredient) => ingredient.ingredientType)
        .length
  );

  const bookingButtonState = computed<AppButtonState>(() =>
    getBookingButtonState(
      bookTripStore.bookingInProgress,
      isEveryIngredientAvailable.value
    )
  );

  // Actions
  function onBookClicked(): void {
    const price = tripSummaryPrice.value;
    assertValue(price, 'tripSummaryStore.ts > onBookClicked > price');

    statsigEventsStore.logTripOverviewBookClicked({
      totalIngredients: recipeStore.amountOfIngredientsInRecipe,
      totalOwnStay: recipeStore.ownStayOnlyRecipe.length,
      totalAccomodation: recipeStore.hotelOnlyRecipe.length,
      totalFerry: recipeStore.ferryOnlyRecipe.length,
      totalFlight: recipeStore.flightOnlyRecipe.length,
      totalActivity: recipeStore.stayOnlyRecipe.flatMap(
        (hotel) => hotel.subIngredients
      ).length,
      market: getMarketOrigin(),
      totalAmount: tripTotalPrice.value,
      priceType:
        price.priceType === TripSummaryPriceType.TotalGross
          ? 'grossDiscount'
          : 'totalPrice',
    });

    const isUserSelectedDate = !!preferredTimeStore.preferredDay;
    if (isUserSelectedDate) {
      bookTripStore.navigateToCheckout();
    } else {
      statsigEventsStore.logTripOverviewContinueWithSelectedDateModalOpened();
      tripOverviewModalsStore.openContinueWithSuggestedDateModal(
        travelDateRange.value,
        itineraryStore.itineraryNumberOfNights
      );
    }
  }

  function openShareWidget(): void {
    shareWidgetStore.openModal();
    statsigEventsStore.logTripOverviewShareOrSaveButtonClicked();
  }

  return {
    // State
    isTripSummaryVisible,

    // Getters
    travelDateRange,
    tripSummaryLoader,
    bookingButtonState,
    tripPriceSegments,
    tripTotalPrice,
    isEveryIngredientAvailable,
    tripSummaryPrice,
    tripGrossPrice,
    tripSummaryCurrency,
    tripTotalAddonsPrice,

    // Actions
    onBookClicked,
    openShareWidget,
  };
});
